import { Component, OnInit, Input, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { LexileScoreFormatPipe, ProductCheck } from '../../_utilities';

import { SearchFilterService, SortOptionService, LimitOptionService, PubDateOptionService } from '../../_services';
import { AppState } from '../../app.service';
import DateFormat from '../../_utilities/date.format';
import { FilterStatusModel } from './filter.status.model';

declare var dataLayer: any;

@Component({
  selector: 'pq-filter-menu',
  templateUrl: './filter.menu.component.html',
  styleUrls: ['./_filter.menu.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FilterMenuComponent implements OnInit {
  public filterStatus: FilterStatusModel = new FilterStatusModel();
  public isFiltersMenuOpen: boolean = false;
  public isSortMenuOpen: boolean = false;
  public iseLibrary: boolean = false;
  public resultsUrl: string = '/results';
  public sortOptions: any = this._sortOptionSvc.get();
  public pubDateFilterActive: boolean = (typeof this.filterStatus.pubDate['pubstartdate'] !== 'undefined');
  public datepickerInputClass: string = 'inline';
  public hasToPubDateFilterError: boolean = false;
  public hasFromPubDateFilterError: boolean = false;
  public dateRangeError: string = '';
  public pubDateOptions: any = this._pubDateOptionSvc.get();
  public pubDateFilterButton: object[] = [];
  public hasPubDateFilter: boolean = (!this._isEmpty(this.filterStatus.pubDate['pubstartdate']) && !this._isEmpty(this.filterStatus.pubDate['pubenddate']));
  public lexileFilterButton: object[] = [];
  public hasLexileScoreFilter: boolean = false;
  public limitToOptions: object[] = [];
  public sourceFilterButton: object[] = [];
  public hasSourceFilter: boolean = this.filterStatus.source !== '';
  public sourceTypesOptions: object[] = [];
  public numFiltersApplied: number = 0;

  // input data from service
  @Input() public suppressSourceTypes: boolean = false;
  @Input() public suppressLimitTo: boolean = false;

  private _subscription: Subscription;
  private _hasLimitFilters: boolean = this.filterStatus.limit.length > 0;
  private _limitFilterButton: object[] = [];

  @Input() set profile (value: string) {
    if (value) {
      this.resultsUrl = '/' + value + '/results';
    }
  }

  @Input() set currencyFilter(value: boolean) {
    this.filterStatus.update({"currencyfilter" : value});
  }

  @Input() set limitTo (options: object[]) {
    this.limitToOptions = options;
    this.updateLimitOptionsState();
  }

  @Input() set sourceTypes (options: object[]) {
    this.sourceTypesOptions = options;
    this.updateSourceTypesState();
  }

  constructor (
    public appState: AppState,
    private _searchFilterSvc: SearchFilterService,
    private _sortOptionSvc: SortOptionService,
    private _limitOptionSvc: LimitOptionService,
    private _pubDateOptionSvc: PubDateOptionService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _productCheck: ProductCheck
  ) {

    if (this.appState.get('sortOrder') && this._sortOptionSvc.isSupportedSort(this.appState.get('sortOrder'))) {
      this._searchFilterSvc.setDefaultSortBy(this.appState.get('sortOrder'));
    } else {
      this._searchFilterSvc.setDefaultSortBy(this._sortOptionSvc.get(1)['value']);
    }

    this._searchFilterSvc.getSortBy().subscribe((sort) => {
      this.filterStatus.update({ sort });
    });

    this._searchFilterSvc.getSourceType().subscribe((source) => {
      if (typeof source !== 'undefined') {
        this.filterStatus.update({ source });
      }
      this.hasSourceFilter = source !== '';
      this.sourceFilterButton = this.updateFilterButtonCollection(this.sourceTypesOptions, [source]);
    });

    this._searchFilterSvc.getLimitTo().subscribe((limit) => {
      this.filterStatus.update({ limit });
      this._hasLimitFilters = limit.length > 0;
      this._limitFilterButton = this.updateFilterButtonCollection(this.limitToOptions, limit);
    });

    this._searchFilterSvc.getCurrencyFilter().subscribe((curr) => {
      this.filterStatus.update({"currencyfilter":curr});
    });

    this._searchFilterSvc.getPublicationDate().subscribe((pubDate) => {
      let startDate = pubDate['pubstartdate'];
      let endDate = pubDate['pubenddate'];

      this.filterStatus.update({ pubDate });
      this.hasPubDateFilter = startDate && endDate;
      this.pubDateFilterButton = this.updatePubDateFilterButtonCollection(this.pubDateOptions, pubDate);

    });

    this._subscription = _route.queryParams.subscribe((queryParam: any) => {
      let sort = this._searchFilterSvc.getDefaultSortBy();
      let status = {};

      if (queryParam['sort']) {
        sort = queryParam['sort'];
      }

      if (typeof queryParam['currencyfilter'] !== 'undefined') {
        // check to see if the string is true because any string value other than empty is true
        status['currencyfilter'] = queryParam['currencyfilter'] === 'true';
      }

      this.lexileFilterButton = [];
      this.hasLexileScoreFilter = (queryParam['lexilemin'] && queryParam['lexilemax']);

      if (this.hasLexileScoreFilter) {
        let lexileScoreFormatPipe = new LexileScoreFormatPipe();
        let title = lexileScoreFormatPipe.transform(queryParam['lexilemin']) + ' to '
          + lexileScoreFormatPipe.transform(queryParam['lexilemax']);

        status['lexilemax'] = queryParam['lexilemax'];
        status['lexilemin'] = queryParam['lexilemin'];

        this.lexileFilterButton.push({
          title,
          value: '&lexilemax=' + queryParam['lexilemax'] + '&lexilemin=' + queryParam['lexilemin']
        });
      }

      status['query'] = queryParam['query'];
      status['sort'] = sort;

      this.appState.set({ searchParams: this.filterStatus.createUrlObject() });

      this.filterStatus.update(status);
    });

    this.filterStatus.status.subscribe((resp: FilterStatusModel) => {
      this.updateAllFilterState();
      this.updateNumFilters();
    });

  }

  public updateSourceTypesState (): void {
    if (this.sourceTypesOptions.length) {
      for (let i = 0, total = this.sourceTypesOptions.length; i < total; i++) {
        let source = this.sourceTypesOptions[i];
        let sourceVal = source['label'];
        let urlOptions = this.filterStatus.createUrlObject();

        // by default all are not active
        source['active'] = false;
        // if service value for source matches set to active
        if (sourceVal.toLowerCase() === this.filterStatus.source.toLowerCase()) {
          source['active'] = true;
        } else {
          urlOptions['source'] = sourceVal;
        }

        // remove the source value to allow for the active link to be clicked again as an undo for the source filter
        if (source['active'] === true) {
          delete urlOptions['source'];
        }

        source['urlOptions'] = urlOptions;
      }
    }
  }

  public updateSortOptionsState (): void {
    for (let i = 0, total = this.sortOptions.length; i < total; i++) {
      let option = this.sortOptions[i];
      let optionVal = option['value'];
      let urlOptions = this.filterStatus.createUrlObject();

      option['active'] = false;

      if (option['value'] !== this.filterStatus.sort) {
        urlOptions['sort'] = option['value'];
      }

      if (optionVal === this.filterStatus.sort) {
        option['active'] = true;
      }

      option['urlOptions'] = urlOptions;
    }
  }

  public updateLimitOptionsState (): void {
    // loop thru the limit options
    for (let i = 0, total = this.limitToOptions.length; i < total; i++) {
      let option = this.limitToOptions[i];
      let optionVal = option['value'];
      let isActive = false; // set default state for option

      // loop thru the active options
      for (let ai = 0, activeTotal = this.filterStatus.limit.length; ai < activeTotal; ai++) {
        let activeValue = this.filterStatus.limit[ai];
        // if option found in active update the state

        if (optionVal === activeValue) {
          isActive = true;
          break;
        }
      }

      // set the state on the current option in the loop
      option['active'] = isActive;
    }
  }

  public updateNumFilters (): void {
    // used for displaying image row within results.
    // pub date and lexile score are not considered filters when changing the display of the UI.
    let hasFiltersApplied = (this.hasSourceFilter || this._hasLimitFilters || this.hasLexileScoreFilter) ? true : false;
    // used for display any extra content within results
    // includes any type of filter
    let hasOneFilterApplied = false;

    this.numFiltersApplied = 0;

    this.numFiltersApplied += this.filterStatus.limit.length;

    if (this.hasPubDateFilter) {
      this.numFiltersApplied++;
    }

    if (this.hasSourceFilter) {
      this.numFiltersApplied++;
    }

    if (this.hasLexileScoreFilter) {
      this.numFiltersApplied++;
    }

    if (this._hasLimitFilters) {
      this.numFiltersApplied++;
    }

    if (this.numFiltersApplied > 0) {
      hasOneFilterApplied = true;
    }

    if (this.numFiltersApplied > 0) {
      // If user selects filters in advance search page
      // or if user do basic search in the results page
      // search usage needs to be logged
      if ((this.appState.get('advanceSearchType')
              && this.appState.get('advanceSearchType') === true)
            || (this.appState.get('basicSearchType')
                  && this.appState.get('basicSearchType') === true)) {
        this.appState.set({ logSearch: true, typeFiltersApplied: hasFiltersApplied, anyFilterApplied: hasOneFilterApplied });
      } else {
        // If user selects filters in the left pane in the results page,
        // search usage should not be logged
        this.appState.set({ logSearch: false, typeFiltersApplied: hasFiltersApplied, anyFilterApplied: hasOneFilterApplied });
      }
    } else {
      // Dont modify logSearch in this else condition. neither true nor false here.
      // If you set true, when users clears the last filter,
      // search usage is getting logged which should not happen.
      // If you set false, when user clicks home button from results page and do another search,
      // search usage is not getting logged which should happen.
      this.appState.set({ typeFiltersApplied: hasFiltersApplied, anyFilterApplied: hasOneFilterApplied });
    }
  }

  public updatePubDateOptionsState (): void {
    for (let i = 0, total = this.pubDateOptions.length; i < total; i++) {
      let option = this.pubDateOptions[i];
      let urlOptions = this.filterStatus.createUrlObject();

      urlOptions['pubstartdate'] = option['fromDate'];
      urlOptions['pubenddate'] = option['toDate'];

      option['active'] = false;

      if ((urlOptions['pubenddate'] === this.filterStatus.pubDate['pubenddate']) &&
        (urlOptions['pubstartdate'] === this.filterStatus.pubDate['pubstartdate'])) {
        option['active'] = true;
      }

      // remove pubstartdate and pubenddate  from the active url to allow for undoing the filter
      if (option['active'] === true) {
        delete urlOptions['pubenddate'];
        delete urlOptions['pubstartdate'];
      }

      option['urlParams'] = urlOptions;
    }
  }

  public updateAllFilterState (): void {
    this.updateSourceTypesState();
    this.updateSortOptionsState();
    this.updateLimitOptionsState();
    this.updatePubDateOptionsState();
  }

  public updateFilterButtonCollection (collection: Object[], state: string[]): Object[] {
    let buttons = [];

    if (collection) {
      for (let i = 0, total = collection.length; i < total; i++) {
        let item = collection[i];
        let itemValue = item['value'] || item['label'];

        for (let is = 0, stateTotal = state.length; is < stateTotal; is++) {
          if (itemValue.toLowerCase() === state[is].toLowerCase()) {
            buttons.push({ label: item['label'], value: itemValue });
            break;
          }
        }
      }
    }
    return buttons;
  }

  public formatDate (value: string): string {
    let date = value;

    if (value.length === 8) {
      date = value.substr(4, 2) + '/' + value.substr(6, 2) + '/' + value.substr(0, 4);
    }

    return date;
  }

  public updatePubDateFilterButtonCollection (collection: Object[], state: Object): Object[] {
    let buttons = [];
    let isFound = false;
    let fromDate;
    let toDate;

    if (collection) {
      for (let i = 0, total = collection.length; i < total; i++) {
        let item = collection[i];

        if (item['fromDate'] === state['pubstartdate'] && item['toDate'] === state['pubenddate']) {
          buttons.push({ title: item['title'] });
          isFound = true;
        }
      }

      // for custom date ranges
      if (!isFound && state['pubstartdate'] && state['pubenddate']) {
        fromDate = this.formatDate(state['pubstartdate']);
        toDate = this.formatDate(state['pubenddate']);

        buttons.push({ title: fromDate + ' - ' + toDate });
      }
    }

    return buttons;
  }

  public ngOnInit (): void {

    // update the number of applied filters
    this.updateNumFilters();

    // update ui for url locations and links classes to display active
    this.updateSourceTypesState();
    this.updateSortOptionsState();
    this.updateLimitOptionsState();

    // update/create collections of selected filters
    this._limitFilterButton = this.updateFilterButtonCollection(this.limitToOptions, this.filterStatus.limit);
    this.sourceFilterButton = this.updateFilterButtonCollection(this.sourceTypesOptions, [this.filterStatus.source]);
    this.pubDateFilterButton = this.updatePubDateFilterButtonCollection(this.pubDateOptions, this.filterStatus.pubDate);
    this.pubDateFilterActive = (typeof this.filterStatus.pubDate['pubstartdate'] !== 'undefined');
    this.iseLibrary = this._productCheck.iseLibraryCheck(this.appState.get('current_profile'));

  }

  // --- template callbacks for events [START] ---
  public toggleMenu (menu: string): void {
    let self = (<any> this);

    if (self['is' + menu + 'Open']) {
      self['is' + menu + 'Open'] = false;
    } else {
      this.isFiltersMenuOpen = false;
      this.isSortMenuOpen = false;

      self['is' + menu + 'Open'] = true;
    }
  }

  public handleClearAllFilters (): void {
    let route = this.resultsUrl;
    this.filterStatus.update({
      limit: [],
      sort: this._searchFilterSvc.getDefaultSortBy(),
      pubDate: { pubenddate: '', pubstartdate: '' },
      source: ''
    });

    this.appState.set({ logSearch: false });
    this._router.navigate([route], { queryParams: this.filterStatus.createUrlObject() });
  }

  public handleLimitFilters (e: any): void {
    const arrayIndex = this.filterStatus.limit.indexOf(e.target.id);
    const route = this.resultsUrl;

    if (e.target.checked && arrayIndex === -1) {
      this.filterStatus.limit.push(e.target.id);
      this.pushResultsSourceLimitToFilter(e.target.id);
    } else if (arrayIndex > -1) {
      this.filterStatus.limit.splice(arrayIndex, 1);
    }

    this._router.navigate([route], { queryParams: this.filterStatus.createUrlObject() });
  }

  public handleCurrencyFilters (e: any): void {
    const filterVal = this.filterStatus.currencyfilter;
    const route = this.resultsUrl;

    if (e.target.checked) {
      this.filterStatus.currencyfilter = true;
    } else {
      this.filterStatus.currencyfilter = false;
    }
    this.pushResultsCurrencyToFilter(filterVal);
    this._router.navigate([route], { queryParams: this.filterStatus.createUrlObject() });
  }

  public handleClearSingleFilter (e: any): void {
    const value = e.currentTarget.value;
    const name = e.currentTarget.name;
    const route =  this.resultsUrl;

    if (name === 'source') {
      this.filterStatus.source = '';
    } else if (name === 'pubDate') {
      this.filterStatus.pubDate = { pubenddate: null, pubstartdate: null };
    } else if (name === 'limit') {
      const arrayIndex = this.filterStatus.limit.indexOf(value);
      if (arrayIndex > -1) {
        this.filterStatus.limit = this.filterStatus.limit.splice(arrayIndex, 1);
      }
    } else if (name === 'lexile') {
      this.filterStatus.lexilemax = null;
      this.filterStatus.lexilemin = null;
    }

    this.appState.set({ logSearch: false });
    this._router.navigate([route], { queryParams: this.filterStatus.createUrlObject() });
  }

  public onSelectFrom (value): void {
    this.filterStatus.update({
      pubDate: {
        pubstartdate: moment(value).format(this._pubDateOptionSvc.dateFormat) || undefined,
        pubenddate: this.filterStatus.pubDate['pubenddate'] || null
      }
    });
    this.pubDateFilterActive = (typeof this.filterStatus.pubDate['pubstartdate'] !== 'undefined');
  }

  public onSelectTo (value): void {
    this.filterStatus.update({
      pubDate: {
        pubstartdate: this.filterStatus.pubDate['pubstartdate'] || null,
        pubenddate: moment(value).format(this._pubDateOptionSvc.dateFormat) || undefined
      }
    });

    this.pubDateFilterActive = (typeof this.filterStatus.pubDate['pubstartdate'] !== 'undefined');
  }

  public doPubDateFilter (): void {
    const route = this.resultsUrl;
    let fromDate = this.filterStatus.pubDate['pubstartdate'];
    let toDate = this.filterStatus.pubDate['pubenddate'] ?
      this.filterStatus.pubDate['pubenddate'] : moment().format(this._pubDateOptionSvc.dateFormat);

    this.hasFromPubDateFilterError = (DateFormat.inputValid(fromDate) === false);
    this.hasToPubDateFilterError = (DateFormat.inputValid(toDate) === false);

    if (!fromDate) {
      this.dateRangeError = this._pubDateOptionSvc.dateErrors['required'];
    } else if (this.hasToPubDateFilterError || this.hasFromPubDateFilterError) {
      this.dateRangeError = this._pubDateOptionSvc.dateErrors['format'];
    } else if (moment(fromDate).isAfter(moment(toDate))) {
      this.dateRangeError = this._pubDateOptionSvc.dateErrors['order'];
    } else {
      this.dateRangeError = '';
    }

    if (this.dateRangeError === '') {
      this.filterStatus.pubDate['pubstartdate'] = fromDate;
      this.filterStatus.pubDate['pubenddate'] = toDate;

      this.pushPubDateRangeFilterToGA(fromDate, toDate);
      this._router.navigate([route], { queryParams: this.filterStatus.createUrlObject() });
    }
  }

  public getCurrencyFilterStatus(): boolean {
    return this.filterStatus.currencyfilter;
  }

  // --- template callbacks for events [END] ---

  public pushResultsSortByFilter(sortByTitle: String) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({'event': 'ResultsSortByFilter', 'label': sortByTitle});
    }
  }

  public pushResultsSourceTypeFilter(sourceType: String) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({'event': 'ResultsSourceTypeFilter', 'label': sourceType});
    }
  }

  public pushPubDateLastFilterToGA(lastTitle: String) {
    dataLayer.push({'event': 'ResultsPubDateFilter', 'label': lastTitle});
  }

  public trackByFn (index: number, item: object): number {
    return item['id'];
  }

  private pushPubDateRangeFilterToGA(fromDate: String, toDate: String) {
    let dateRange = fromDate + '-' + toDate;
    dataLayer.push({'event': 'ResultsPubDateFilter', 'label': 'Fields', 'position': dateRange });
  }

  private pushResultsSourceLimitToFilter(sourceFilter: String) {
    dataLayer.push({'event': 'ResultsSourceLimitToFilter', 'label': sourceFilter});
  }

  private pushResultsCurrencyToFilter(currencyFilter: boolean) {
    dataLayer.push({'event': 'ResultsCurrencyFilter', 'label': 'currencyFilter', 'value': currencyFilter});
  }

  private _isEmpty (val: string): boolean {
    return (val === undefined || val == null || val.length <= 0) ? true : false;
  }
}
